import { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useServices,
  useService,
  useActiveServiceTypes,
  useDeleteService,
  useBulkDeleteServices,
  useBulkUpdateServices,
} from '../../services/httpServices/queries';
import { useDebounce } from '../../hooks/useDebounce';
import { toast } from 'sonner';
import { Pagination } from '../../components/ui/Pagination';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { ServiceFormModal } from '../../components/ui/ServiceFormModal';
import { defaultUnitLabel } from '../../utils/servicePricing';
import {
  Tags,
  Loader2,
  AlertCircle,
  Plus,
  Pencil,
  Trash2,
  Search,
  X,
  Power,
  PowerOff,
  Square,
  SquareCheck,
  Edit3,
  CheckCircle,
  XCircle,
  Tag,
  DollarSign,
  Stethoscope,
  Percent,
  List,
} from 'lucide-react';

export function Services() {
  const navigate = useNavigate();
  const location = useLocation();
  const limit = 20;

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const [page, setPage] = useState(() => {
    const p = params.get('page');
    return p ? Math.max(1, parseInt(p, 10)) : 1;
  });
  const [search, setSearch] = useState(() => params.get('search') || '');
  const debouncedSearch = useDebounce(search, 350);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [typeFilter, setTypeFilter] = useState(() => params.get('serviceType') || 'all');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [viewModal, setViewModal] = useState<{ open: boolean; id: string } | null>(null);
  const [formModal, setFormModal] = useState<{ open: boolean; id?: string }>({ open: false });
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  const { data, isLoading, error } = useServices(
    page,
    limit,
    debouncedSearch || undefined,
    typeFilter !== 'all' ? typeFilter : undefined
  );
  const deleteMutation = useDeleteService();
  const bulkDeleteMutation = useBulkDeleteServices();
  const bulkUpdateMutation = useBulkUpdateServices();
  const { data: viewData, isLoading: viewLoading, error: viewError } = useService(
    viewModal?.open ? viewModal.id : undefined
  );

  const service = viewData?.data;

  const services = data?.data || [];
  const meta = data?.meta;
  const trashedCount = Number(data?.stats?.trashed ?? 0);

  // Resolve the stored serviceType code to its managed label (falls back to the
  // raw code for legacy values that are not in the catalogue).
  const { data: serviceTypesData } = useActiveServiceTypes();
  const serviceTypeName = (code?: string) =>
    serviceTypesData?.data?.find((type) => type.code === code)?.name || code || '—';

  const filteredServices = useMemo(() => {
    if (statusFilter === 'all') return services;
    return services.filter((svc: any) =>
      statusFilter === 'active' ? svc.isActive : !svc.isActive
    );
  }, [services, statusFilter]);

  useEffect(() => {
    const newParams = new URLSearchParams();
    if (page > 1) newParams.set('page', String(page));
    if (debouncedSearch) newParams.set('search', debouncedSearch);
    if (typeFilter !== 'all') newParams.set('serviceType', typeFilter);

    const query = newParams.toString();
    navigate(`${location.pathname}${query ? `?${query}` : ''}`, { replace: true });
  }, [page, debouncedSearch, typeFilter, navigate, location.pathname]);

  useEffect(() => {
    setPage(1);
  }, [search, typeFilter]);

  const allSelected = filteredServices.length > 0 && filteredServices.every((svc: any) => selectedIds.includes(svc.id));
  const someSelected = selectedIds.length > 0;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !filteredServices.some((svc: any) => svc.id === id)));
    } else {
      const newIds = filteredServices.map((svc: any) => svc.id);
      setSelectedIds((prev) => Array.from(new Set([...prev, ...newIds])));
    }
  };

  const toggleSelectOne = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleBulkDelete = () => {
    if (selectedIds.length === 0) return;
    setBulkDeleteOpen(true);
  };

  const handleConfirmBulkDelete = async () => {
    const ids = [...selectedIds];
    try {
      await bulkDeleteMutation.mutateAsync(ids);
      setSelectedIds([]);
      toast.success(`Deleted ${ids.length} service${ids.length === 1 ? '' : 's'}`);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to delete services');
    } finally {
      setBulkDeleteOpen(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      toast.success('Service deleted');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to delete service');
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleBulkActivate = async () => {
    const ids = [...selectedIds];
    try {
      await bulkUpdateMutation.mutateAsync({ ids, data: { isActive: true } });
      setSelectedIds([]);
      toast.success(`Activated ${ids.length} service${ids.length === 1 ? '' : 's'}`);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to activate services');
    }
  };

  const handleBulkDeactivate = async () => {
    const ids = [...selectedIds];
    try {
      await bulkUpdateMutation.mutateAsync({ ids, data: { isActive: false } });
      setSelectedIds([]);
      toast.success(`Deactivated ${ids.length} service${ids.length === 1 ? '' : 's'}`);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to deactivate services');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
        <AlertCircle size={18} />
        <span>Failed to load services</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Tags className="w-6 h-6 text-primary" />
          <h1 className="text-2xl font-bold text-text">Services</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/administration/services/trash')}
            className="px-4 py-2 border border-border text-text-muted hover:text-danger hover:border-danger rounded-lg transition-colors text-sm font-medium flex items-center gap-2"
            title="View trash"
          >
            <Trash2 size={16} />
            Trash
            {trashedCount > 0 && (
              <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-danger text-white text-[0.625rem] font-semibold leading-none">
                {trashedCount > 99 ? '99+' : trashedCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setFormModal({ open: true })}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2"
          >
            <Plus size={16} />
            Add Service
          </button>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="bg-surface rounded-xl border border-border p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="text"
              placeholder="Search by name, code or type..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>
          <select
            value={typeFilter}
            onChange={(e) => { setTypeFilter(e.target.value); setSelectedIds([]); }}
            className="px-3 py-2 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
          >
            <option value="all">All Types</option>
            {(serviceTypesData?.data || []).map((type) => (
              <option key={type.id} value={type.code}>
                {type.name}
              </option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value as any); setSelectedIds([]); }}
            className="px-3 py-2 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          {search && (
            <button
              onClick={() => { setSearch(''); setPage(1); }}
              className="px-3 py-2 text-text-muted hover:text-danger border border-border hover:border-danger rounded-lg transition-colors text-sm flex items-center gap-1.5 whitespace-nowrap"
            >
              <X size={14} />
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Bulk Actions */}
      {someSelected && (
        <div className="flex items-center gap-2 bg-surface border border-border rounded-xl p-3">
          <span className="text-sm text-text-muted mr-2">{selectedIds.length} selected</span>
          <button
            onClick={handleBulkActivate}
            disabled={bulkUpdateMutation.isPending}
            className="px-3 py-1.5 rounded-lg bg-success text-white text-xs font-medium hover:bg-success/90 disabled:opacity-50 flex items-center gap-1.5"
          >
            <Power size={12} /> Activate
          </button>
          <button
            onClick={handleBulkDeactivate}
            disabled={bulkUpdateMutation.isPending}
            className="px-3 py-1.5 rounded-lg bg-warning text-white text-xs font-medium hover:bg-warning/90 disabled:opacity-50 flex items-center gap-1.5"
          >
            <PowerOff size={12} /> Deactivate
          </button>
          <button
            onClick={handleBulkDelete}
            disabled={bulkDeleteMutation.isPending}
            className="px-3 py-1.5 rounded-lg bg-danger text-white text-xs font-medium hover:bg-danger/90 disabled:opacity-50 flex items-center gap-1.5"
          >
            <Trash2 size={12} /> Delete
          </button>
        </div>
      )}

      <div className="bg-surface rounded-xl border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-background/50">
                <th className="px-4 py-3 text-left w-10">
                  <button onClick={toggleSelectAll} className="text-text-muted hover:text-text transition-colors">
                    {allSelected ? <SquareCheck size={18} className="text-primary" /> : <Square size={18} />}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Code</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Price</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Tax</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredServices.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-text-muted">
                    {search || statusFilter !== 'all' || typeFilter !== 'all' ? (
                      <span>No services match your filters.{' '}
                        <button onClick={() => { setSearch(''); setStatusFilter('all'); setTypeFilter('all'); setPage(1); }} className="text-primary hover:underline">
                          Clear filters
                        </button>
                      </span>
                    ) : (
                      'No services configured. Add your first service to get started.'
                    )}
                  </td>
                </tr>
              ) : (
                filteredServices.map((svc: any) => (
                  <tr key={svc.id} className="hover:bg-background/50 transition-colors">
                    <td className="px-4 py-3">
                      <button onClick={() => toggleSelectOne(svc.id)} className="text-text-muted hover:text-text transition-colors">
                        {selectedIds.includes(svc.id) ? <SquareCheck size={18} className="text-primary" /> : <Square size={18} />}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-text">
                      <button
                        onClick={() => setViewModal({ open: true, id: svc.id })}
                        className="hover:text-primary transition-colors text-left cursor-pointer"
                      >
                        {svc.name}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-sm font-mono text-text">{svc.code}</td>
                    <td className="px-4 py-3 text-sm text-text">{serviceTypeName(svc.serviceType)}</td>
                    <td className="px-4 py-3 text-sm text-text">
                      ৳{svc.price?.toLocaleString()}
                      {svc.billingUnit && svc.billingUnit !== 'flat' && (
                        <span className="text-text-muted">
                          {' '}/ {svc.unitLabel?.trim() || defaultUnitLabel(svc.billingUnit)}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-text-muted">{svc.taxPercent || 0}%</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                        svc.isActive ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'
                      }`}>
                        {svc.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => setFormModal({ open: true, id: svc.id })}
                          className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors"
                          title="Edit"
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          onClick={() => setDeleteTarget({ id: svc.id, name: svc.name })}
                          className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-danger transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          page={page}
          totalPages={meta?.totalPages || 1}
          total={meta?.total || 0}
          limit={limit}
          onPageChange={setPage}
          itemName="services"
        />
      </div>

      {/* View Modal */}
      {viewModal?.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-surface border border-border rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-surface z-10 flex items-center justify-between p-5 border-b border-border">
              <div className="flex items-center gap-3">
                <Tags className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-bold text-text">Service Details</h2>
              </div>
              <button
                onClick={() => setViewModal(null)}
                className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-text transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            {viewLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : viewError || !service ? (
              <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger m-5">
                <AlertCircle size={18} />
                <span>Failed to load service details. Please try again.</span>
              </div>
            ) : (
              <div className="p-5 space-y-5">
                {/* Header Card */}
                <div className="bg-surface border border-border rounded-xl p-6">
                  <div className="flex flex-col md:flex-row gap-6 items-start">
                    <div className="flex-1 min-w-0 space-y-4">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <div className="flex items-center gap-2">
                            <Tags size={20} className="text-primary" />
                            <h1 className="text-2xl font-bold text-text">{service.name}</h1>
                          </div>
                          <p className="text-sm text-text-muted mt-1 font-mono">{service.code}</p>
                        </div>
                        <span
                          className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium shrink-0 ${
                            service.isActive
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                          }`}
                        >
                          {service.isActive ? <CheckCircle size={12} /> : <XCircle size={12} />}
                          {service.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div className="flex items-center gap-3 bg-background rounded-lg p-3 border border-border">
                          <div className="p-2 bg-primary/10 rounded-lg">
                            <Tag size={16} className="text-primary" />
                          </div>
                          <div>
                            <p className="text-xs text-text-muted">Service Type</p>
                            <p className="text-sm font-medium text-text">{serviceTypeName(service.serviceType)}</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3 bg-background rounded-lg p-3 border border-border">
                          <div className="p-2 bg-primary/10 rounded-lg">
                            <DollarSign size={16} className="text-primary" />
                          </div>
                          <div>
                            <p className="text-xs text-text-muted">
                              {service.billingUnit && service.billingUnit !== 'flat' ? 'Rate' : 'Price'}
                            </p>
                            <p className="text-sm font-medium text-text">
                              ৳{service.price?.toLocaleString()}
                              {service.billingUnit && service.billingUnit !== 'flat' && (
                                <span className="text-text-muted text-xs">
                                  {' '}/ {service.unitLabel?.trim() || defaultUnitLabel(service.billingUnit)}
                                </span>
                              )}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3 bg-background rounded-lg p-3 border border-border">
                          <div className="p-2 bg-primary/10 rounded-lg">
                            <Tag size={16} className="text-primary" />
                          </div>
                          <div>
                            <p className="text-xs text-text-muted">Billing Unit</p>
                            <p className="text-sm font-medium text-text">
                              {service.billingUnit && service.billingUnit !== 'flat'
                                ? (service.unitLabel?.trim() || defaultUnitLabel(service.billingUnit))
                                : 'Flat (one-off)'}
                              {service.minimumUnits ? (
                                <span className="text-text-muted text-xs"> · min {Number(service.minimumUnits)}</span>
                              ) : null}
                              {service.unitIncrement ? (
                                <span className="text-text-muted text-xs"> · {Number(service.unitIncrement)}m step</span>
                              ) : null}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3 bg-background rounded-lg p-3 border border-border">
                          <div className="p-2 bg-primary/10 rounded-lg">
                            <Percent size={16} className="text-primary" />
                          </div>
                          <div>
                            <p className="text-xs text-text-muted">Tax</p>
                            <p className="text-sm font-medium text-text">{service.taxPercent || 0}%</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3 bg-background rounded-lg p-3 border border-border">
                          <div className="p-2 bg-primary/10 rounded-lg">
                            <Stethoscope size={16} className="text-primary" />
                          </div>
                          <div>
                            <p className="text-xs text-text-muted">Requires Doctor</p>
                            <p className="text-sm font-medium text-text">{service.requiresDoctor ? 'Yes' : 'No'}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div className="bg-surface border border-border rounded-xl p-6 space-y-4">
                  <div className="flex items-center gap-2">
                    <List size={18} className="text-primary" />
                    <h2 className="text-lg font-semibold text-text">Description</h2>
                  </div>
                  <p className="text-sm text-text">{service.description || 'No description provided.'}</p>
                </div>

                {/* Bottom Actions */}
                <div className="flex flex-wrap items-center justify-end gap-3 pt-4">
                  <button
                    onClick={() => {
                      setViewModal(null);
                      setFormModal({ open: true, id: service.id });
                    }}
                    className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2"
                  >
                    <Edit3 size={16} />
                    Edit Service
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <ServiceFormModal
        isOpen={formModal.open}
        serviceId={formModal.id}
        onClose={() => setFormModal({ open: false })}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Delete service"
        description={
          <>
            Are you sure you want to delete <strong>{deleteTarget?.name}</strong>? It will be moved to trash.
          </>
        }
        loading={deleteMutation.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <ConfirmDialog
        open={bulkDeleteOpen}
        title={`Delete ${selectedIds.length} service${selectedIds.length === 1 ? '' : 's'}?`}
        description="The selected services will be moved to trash. This cannot be undone."
        confirmLabel={`Delete ${selectedIds.length}`}
        loading={bulkDeleteMutation.isPending}
        onConfirm={handleConfirmBulkDelete}
        onCancel={() => setBulkDeleteOpen(false)}
      />
    </div>
  );
}
